import { useMemo, useState } from 'react';
import { DeleteButton } from '@/shared/ui/list/DeleteButton';
import { GenericListPage } from '@/shared/ui/list/GenericListPage';
import type { ListPageConfig, ListRow } from '@/shared/ui/list/types';
import { EMPTY_PRIZE, deletePrize, fetchPrizeList, prizeToValues, type PrizeItem } from './api';
import { PrizeModal } from './PrizeModal';
import { PrizeSectorsPanel } from './PrizeSectorsPanel';

const LIST_KEY = 'raffle-prizes';

interface Prize extends ListRow, PrizeItem {
  image?: { id?: number; thumb_140_140?: string } | null;
  winner_count?: number;
  is_active?: boolean | number;
}

const ACTIVITY_OPTIONS = [
  { value: '1', label: 'Разыгрывается' },
  { value: '0', label: 'Не разыгрывается' },
];

const PHOTO_SIZE = 60;

type Dialog = { kind: 'create' } | { kind: 'edit'; prize: Prize } | null;

export const PrizesPage = () => {
  const [dialog, setDialog] = useState<Dialog>(null);

  const config = useMemo<ListPageConfig<Prize>>(
    () => ({
      queryKey: LIST_KEY,
      title: 'Призы',
      errorText: 'Ошибка загрузки призов',
      fetchList: fetchPrizeList,
      rows: ['data'],
      headerActions: (
        <button type="button" className="btn btn-primary" onClick={() => setDialog({ kind: 'create' })}>
          Добавить новый приз
        </button>
      ),
      summary: () => <PrizeSectorsPanel />,
      filters: [{ name: 'is_active', label: 'Статус розыгрыша', kind: 'select', options: ACTIVITY_OPTIONS }],
      columns: [
        { header: 'ID', render: (prize) => prize.id },
        {
          header: 'Фото',
          render: (prize) =>
            prize.image?.thumb_140_140 && (
              <img src={prize.image.thumb_140_140} width={PHOTO_SIZE} alt={String(prize.image.id ?? '')} />
            ),
        },
        { header: 'Название приза', render: (prize) => prize.name },
        { header: 'Сколько пользователей выиграли приз', render: (prize) => prize.winner_count ?? 0 },
        {
          header: 'Статус розыгрыша',
          render: (prize) => (prize.is_active ? 'Разыгрывается' : 'Не разыгрывается'),
        },
        {
          header: 'Действия',
          render: (prize) => (
            <>
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={() => setDialog({ kind: 'edit', prize })}
              >
                <i className="fa fa-pencil" />
              </button>{' '}
              <DeleteButton listKey={LIST_KEY} remove={() => deletePrize(prize.id)} confirmText="Вы уверены, что хотите удалить приз?" />
            </>
          ),
        },
      ],
    }),
    [],
  );

  const close = () => setDialog(null);

  return (
    <>
      <GenericListPage config={config} />
      {dialog?.kind === 'create' && <PrizeModal initial={EMPTY_PRIZE} onClose={close} />}
      {dialog?.kind === 'edit' && (
        <PrizeModal id={dialog.prize.id} initial={prizeToValues(dialog.prize)} onClose={close} />
      )}
    </>
  );
};
